import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ClientHeaderNavigation from '../../components/ui/ClientHeaderNavigation';
import BookingProgressNavigation from '../../components/ui/BookingProgressNavigation';
import CalendarHeader from './components/CalendarHeader';
import CalendarGrid from './components/CalendarGrid';
import AvailabilityLegend from './components/AvailabilityLegend';
import QuickDateSelector from './components/QuickDateSelector';
import Button from '../../components/ui/Button';
import Modal from '../../components/ui/Modal';
import { useBooking } from '../../components/context/BookingContext';
import { useService } from '../../components/context/ServiceContext';
import repositoryContainer from '../../services/RepositoryContainer';
import { useCompany } from '../../components/context/CompanyContext';


const DateSelection = () => {
  const navigate = useNavigate();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [calendarDates, setCalendarDates] = useState([]);
  const { selectedDate, setSelectedDate, resetBooking } = useBooking();
  const { selectedServices } = useService();
    const { company} = useCompany();
  
  const { companyPath } = useParams();

  const [availability, setAvailability] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [showRequestFallback, setShowRequestFallback] = useState(false);
  const selectedService = selectedServices?.[0] || null;

  const formatPhoneNumber = (phoneNumberString) => {
  if (!phoneNumberString) return '';
  
  // Remove any non-numerical characters just in case
  const cleaned = ('' + phoneNumberString).replace(/\D/g, '');
  
  // Pattern for 059 580 3990
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  
  if (match) {
    return `${match[1]} ${match[2]} ${match[3]}`;
  }
  
  return phoneNumberString; // Return original if it doesn't match the expected length
};

  useEffect(() => {
    if (company?.id) {
      loadAvailability(currentMonth);
    }
  }, [currentMonth, company?.id, selectedService?.id]);

  const loadAvailability = async (month) => {
    setIsLoading(true);
    setShowRequestFallback(false);
    try {
      const year = month.getFullYear();
      const monthIndex = month.getMonth();
      const startDate = new Date(year, monthIndex, 1).toISOString().split('T')[0];
      const endDate = new Date(year, monthIndex + 1, 0).toISOString().split('T')[0];

      const data = await repositoryContainer.dateSlot.getClientAvailability(
        startDate,
        endDate,
        company?.id // Replace with actual company ID as needed
      );

      const nextAvailability = data?.availability || {};
      setAvailability(nextAvailability);
      setShowRequestFallback(Boolean(selectedService?.id) && Object.keys(nextAvailability).length === 0);
      return nextAvailability;
    } catch (error) {
      console.error('Failed to load availability:', error);
      setAvailability({});
      setShowRequestFallback(false);
      return {};
    } finally {
      setIsLoading(false);
    }
  };

  const mockAvailability = {
    "2025-01-02": true,
    "2025-01-03": true,
    "2025-01-04": false,
    "2025-01-05": true,
    "2025-01-06": true,
    "2025-01-07": true,
    "2025-01-08": true,
    "2025-01-09": true,
    "2025-01-10": false,
    "2025-01-11": true,
    "2025-01-12": true,
    "2025-01-13": true,
    "2025-01-14": true,
    "2025-01-15": true,
    "2025-01-16": true,
    "2025-01-17": false,
    "2025-01-18": true,
    "2025-01-19": true,
    "2025-01-20": true,
    "2025-01-21": true,
    "2025-01-22": true,
    "2025-01-23": true,
    "2025-01-24": false,
    "2025-01-25": true,
    "2025-01-26": true,
    "2025-01-27": true,
    "2025-01-28": true,
    "2025-01-29": true,
    "2025-01-30": true,
    "2025-01-31": false,
    "2025-02-01": true,
    "2025-02-02": true,
    "2025-02-03": true,
    "2025-02-04": true,
    "2025-02-05": true,
    "2025-02-06": true,
    "2025-02-07": false,
    "2025-02-08": true,
    "2025-02-09": true,
    "2025-02-10": true,
    "2025-02-11": true,
    "2025-02-12": true,
    "2025-02-13": true,
    "2025-02-14": false,
    "2025-02-15": true,
    "2025-02-16": true,
    "2025-02-17": true,
    "2025-02-18": true,
    "2025-02-19": true,
    "2025-02-20": true,
    "2025-02-21": false,
    "2026-03-22": true,
    "2026-03-23": true,
    "2026-03-24": true,
    "2026-03-25": true,
    "2026-03-26": true,
    "2026-03-27": true,
    "2026-03-28": false
  };

  useEffect(() => {
    generateCalendarDates(currentMonth);
  }, [currentMonth, availability]);

  const generateCalendarDates = (month) => {
    const year = month?.getFullYear();
    const monthIndex = month?.getMonth();
    const firstDay = new Date(year, monthIndex, 1);
    const lastDay = new Date(year, monthIndex + 1, 0);
    const startingDayOfWeek = firstDay?.getDay();
    const today = new Date();
    today?.setHours(0, 0, 0, 0);

    const dates = [];

    for (let i = 0; i < startingDayOfWeek; i++) {
      const prevMonthDate = new Date(year, monthIndex, -startingDayOfWeek + i + 1);
      dates.push({
        date: prevMonthDate,
        isCurrentMonth: false,
        isAvailable: false,
        dateSlotId: null
      });
    }

    for (let day = 1; day <= lastDay?.getDate(); day++) {
      const date = new Date(year, monthIndex, day);
      const dateKey = `${year}-${String(monthIndex + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const isPast = date < today;

      // availability[dateKey] is now { isAvailable: bool, dateSlotId: string }
      const availabilityEntry = availability?.[dateKey];
      const isAvailableFromApi = Boolean(
        availabilityEntry?.isAvailable && availabilityEntry?.dateSlotId
      );

      dates.push({
        date,
        isCurrentMonth: true,
        isAvailable: !isPast && isAvailableFromApi,
        dateSlotId: availabilityEntry?.dateSlotId ?? null
      });
    }

    const remainingDays = 42 - dates?.length;
    for (let i = 1; i <= remainingDays; i++) {
      const nextMonthDate = new Date(year, monthIndex + 1, i);
      dates.push({
        date: nextMonthDate,
        isCurrentMonth: false,
        isAvailable: false,
        dateSlotId: null
      });
    }

    setCalendarDates(dates);
  };

  const handlePreviousMonth = () => {
    const newMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1);
    setCurrentMonth(newMonth);
  };

  const handleNextMonth = () => {
    const newMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1);
    setCurrentMonth(newMonth);
  };

  const canGoPrevious = () => {
    const today = new Date();
    const firstDayOfCurrentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const firstDayOfDisplayedMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
    return firstDayOfDisplayedMonth > firstDayOfCurrentMonth;
  };

  const canGoNext = () => {
    const maxMonth = new Date();
    maxMonth?.setMonth(maxMonth?.getMonth() + 3);
    const lastDayOfDisplayedMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
    return lastDayOfDisplayedMonth < maxMonth;
  };

  const handleDateSelect = (date, dateSlotId = null) => {
    const dateKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    const availabilityEntry = availability?.[dateKey];
    const resolvedDateSlotId = dateSlotId ?? availabilityEntry?.dateSlotId ?? null;
    const isAvailable = Boolean(availabilityEntry?.isAvailable && resolvedDateSlotId);

    if (!isAvailable) return;

    setSelectedDate(date, resolvedDateSlotId);

    localStorage.setItem('selectedDate', date?.toISOString());
  };

  const handleQuickDateSelect = async (date) => {
    const newMonth = new Date(date.getFullYear(), date.getMonth(), 1);
    const dateKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    const isDisplayedMonth = currentMonth.getFullYear() === newMonth.getFullYear()
      && currentMonth.getMonth() === newMonth.getMonth();

    setCurrentMonth(newMonth);
    const monthAvailability = isDisplayedMonth
      ? availability
      : await loadAvailability(newMonth);
    const availabilityEntry = monthAvailability?.[dateKey];

    if (!availabilityEntry?.isAvailable || !availabilityEntry?.dateSlotId) return;

    setSelectedDate(date, availabilityEntry.dateSlotId);
    localStorage.setItem('selectedDate', date.toISOString());
  };

  const handleContinue = () => {
    if (selectedDate) {
      navigate(`/${companyPath}/time-slot-selection`);
    }
  };

  const handleAddToRequest = () => {
    if (!selectedService?.id) return;

    navigate(`/${companyPath}`, {
      state: {
        requestService: selectedService,
        openRequestDrawer: window.matchMedia('(min-width: 768px)').matches,
      },
    });
  };

  const handleRestartBooking = () => {
    setSelectedDate(null);
    resetBooking();
    setCurrentMonth(new Date());
  };

  return (
    <div className="min-h-screen bg-background">
      <ClientHeaderNavigation onRestartBooking={handleRestartBooking} />
      <div className="pt-20 md:pt-24">
        <BookingProgressNavigation
          currentStep={1}
          totalSteps={3}
          canGoBack={true}
          canGoForward={selectedDate !== null}
          onForward={handleContinue}
          steps={[
            { label: 'Select Date', path: `/${companyPath}/date-selection` },
            { label: 'Choose Time', path: `/${companyPath}/time-slot-selection` },
            { label: 'Your Details', path: `/${companyPath}/client-details-form` }
          ]}
        />
      </div>
      <main className="max-w-6xl mx-auto px-4 md:px-6 lg:px-8 py-8 md:py-12 lg:py-16">
        <div className="text-center mb-8 md:mb-12 lg:mb-16">
          <h1 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-foreground mb-3 md:mb-4">
            Choose Your Perfect Day
          </h1>
          <p className="text-base md:text-lg lg:text-xl text-muted-foreground font-caption max-w-2xl mx-auto">
            Select a date that works best for your appointment. 
          </p>
        </div>

        <QuickDateSelector onSelectDate={handleQuickDateSelect} />

        <CalendarHeader
          currentMonth={currentMonth}
          onPreviousMonth={handlePreviousMonth}
          onNextMonth={handleNextMonth}
          canGoPrevious={canGoPrevious()}
          canGoNext={canGoNext()}
        />

        <CalendarGrid
          dates={calendarDates}
          selectedDate={selectedDate}
          onDateSelect={(date, dateSlotId) => handleDateSelect(date, dateSlotId)}
          isLoading={isLoading}
        />

        <AvailabilityLegend />

        {selectedDate && (
          <div className="mt-8 md:mt-10 lg:mt-12 bg-gradient-to-br from-primary/10 to-secondary/10 rounded-xl md:rounded-2xl p-6 md:p-8 border border-primary/20">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 md:gap-6">
              <div className="text-center md:text-left">
                <p className="text-sm md:text-base font-caption text-muted-foreground mb-1">
                  Selected Date
                </p>
                <p className="font-heading font-semibold text-2xl md:text-3xl text-foreground">
                  {selectedDate?.toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              </div>
              <Button
                variant="default"
                size="lg"
                onClick={handleContinue}
                iconName="ArrowRight"
                iconPosition="right"
                className="w-full md:w-auto"
              >
                Continue to Time Selection
              </Button>
            </div>
          </div>
        )}

        <div className="mt-8 md:mt-10 lg:mt-12 text-center">
          <p className="text-sm md:text-base text-muted-foreground font-caption">
            Need help choosing a date? Call us at{' '}
            <a href="tel:+15551234567" className="text-primary hover:underline font-medium">
              {formatPhoneNumber(company?.phoneNumber)}
            </a>
          </p>
        </div>
      </main>

      <Modal
        isOpen={showRequestFallback}
        onClose={() => setShowRequestFallback(false)}
        title="No dates available"
        compactHeader
        footer={(
          <Button fullWidth iconName="Plus" onClick={handleAddToRequest}>
            Add Request
          </Button>
        )}
      >
        <p className="text-center font-caption text-sm text-muted-foreground">
          Send us a request instead.
        </p>
      </Modal>
    </div>
  );
};

export default DateSelection;
